import { parsePhoneNumberFromString } from 'libphonenumber-js/mobile';

/**
 * Apply default country code to a phone number intelligently using libphonenumber-js.
 *
 * Handles these scenarios:
 *  1. Already has '+'              → leave as-is (e.g. +919876543210)
 *  2. Parses as valid international
 *     number with matching CC      → country code already present, just add '+'
 *                                     (e.g. 919876543210 with CC 91 → +919876543210)
 *  3. Doesn't have CC              → prepend '+' + country code
 *                                     (e.g. 9876543210 with CC 91 → +919876543210)
 *  4. Leading zeros                → stripped first (local trunk prefix)
 *
 * Uses Google's libphonenumber (via libphonenumber-js) for accurate detection
 * instead of heuristic length checks.
 */
export function applyCountryCode(phone: string, countryCode: string | undefined): string {
  const trimmed = phone.trim();
  if (!countryCode || trimmed.startsWith('+')) return trimmed;
  // A WhatsApp username user ID ("US.1349…") is not a phone — never add a CC.
  if (isWaUserId(trimmed)) return trimmed;

  // Keep only digits
  const digits = trimmed.replace(/\D/g, '');
  // Strip leading zeros (local trunk prefix like 0 in many countries)
  const stripped = digits.replace(/^0+/, '');
  if (!stripped) return trimmed;

  const cc = countryCode.replace(/\D/g, '');
  if (!cc) return `+${stripped}`;

  // Try parsing as an international number (with + prefix).
  // If it's valid and its calling code matches the configured one, CC is already present.
  const asInternational = parsePhoneNumberFromString(`+${stripped}`);
  if (asInternational?.isValid() && asInternational.countryCallingCode === cc) {
    return `+${stripped}`;
  }

  // Otherwise prepend the configured country code
  return `+${cc}${stripped}`;
}

/**
 * True when `+<digits>` parses as a valid international number for ANY
 * country — i.e. the digits already carry their country code. Used to avoid
 * prepending the default CC to a full number typed without the '+'.
 */
export function isFullInternationalNumber(digits: string): boolean {
  if (!digits) return false;
  const parsed = parsePhoneNumberFromString(`+${digits}`);
  return !!parsed?.isValid();
}

/**
 * Prepare a dialled number for Twilio — Singapore only.
 *
 * Twilio treats a number without a leading '+' as US and prepends +1, so a bare
 * Singapore number like `6595422149` was dialled as `+16595422149`. We fix only
 * Singapore: if the bare number is a valid Singapore (+65) number once a '+' is
 * added, we prepend it. Every other number — including bare US numbers — is left
 * completely unchanged, so Twilio's existing behaviour and US users are unaffected.
 *
 *   • already starts with '+'      → sent unchanged
 *   • valid Singapore number       → '+' prepended (e.g. 6595422149 → +6595422149)
 *   • anything else (incl. US)     → sent unchanged
 */
export function toDialE164(raw: string): string {
  const trimmed = (raw || '').trim();
  if (!trimmed || trimmed.startsWith('+')) return trimmed;

  const digits = trimmed.replace(/\D/g, '');
  const parsed = parsePhoneNumberFromString(`+${digits}`);
  if (parsed?.isValid() && parsed.country === 'SG') return `+${digits}`;

  return trimmed;
}

/**
 * Meta WhatsApp business-scoped user ID (BSUID) — identifies a username-only
 * WhatsApp user whose phone number is hidden. Format per Meta: ISO country
 * code + "." + up to 128 alphanumerics (e.g. "US.13491208655302741918");
 * parent IDs add "ENT." (e.g. "US.ENT.11815799212886844830").
 * No phone number can match this, so it is safe to call on any contact ID.
 */
const WA_USER_ID_PATTERN = /^[A-Z]{2}\.(ENT\.)?[A-Za-z0-9]{1,128}$/;

export function isWaUserId(value: string | null | undefined): boolean {
  return WA_USER_ID_PATTERN.test((value ?? '').trim());
}

/** Contact ID as stored in the inbox: a BSUID is kept verbatim, anything
 *  else is reduced to digits only (the long-standing phone behaviour). */
export function toContactId(value: string | null | undefined): string {
  const s = (value ?? '').trim();
  return isWaUserId(s) ? s : s.replace(/\D/g, '');
}

const WA_USER_EMAIL_DOMAIN = 'wa-user.ulgebra.com';

/**
 * CRM sync has no phone for a WhatsApp username user, so the server stores an
 * email built from the user ID instead ("US.1349…@wa-user.ulgebra.com").
 * Returns that email for a user ID, or '' for anything else.
 */
export function waUserIdToEmail(value: string | null | undefined): string {
  const s = (value ?? '').trim();
  return isWaUserId(s) ? `${s}@${WA_USER_EMAIL_DOMAIN}` : '';
}

/** Reverse of waUserIdToEmail: the user ID behind a CRM dummy email, or ''.
 *  CRMs may lowercase emails, so the country code (and "ENT") is upper-cased back. */
export function waUserIdFromEmail(email: string | null | undefined): string {
  const m = /^([a-z]{2})\.(ent\.)?([a-z0-9]{1,128})@wa-user\.ulgebra\.com$/i.exec((email ?? '').trim());
  if (!m) return '';
  return `${m[1].toUpperCase()}.${m[2] ? 'ENT.' : ''}${m[3]}`;
}
